import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { exportToExcel } from '@/utils/exportUtils';
import { mockForecasts, mockSalesTrends, type MockForecast } from '@/mocks/forecasting';

type ForecastTab = 'forecasts' | 'trends' | 'recommendations';

export default function AIForecastingPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ForecastTab>('forecasts');
  const [lookbackMonths, setLookbackMonths] = useState(6);
  const [forecasts] = useState<MockForecast[]>(mockForecasts);

  // Summary Metrics
  const avgAccuracy = '88.4%';
  const totalPredictedQty = forecasts.reduce((sum, f) => sum + f.predictedQty, 0);
  const totalProductionQty = forecasts.reduce((sum, f) => sum + f.suggestedProductionQty, 0);
  const totalPurchaseQty = forecasts.reduce((sum, f) => sum + f.suggestedPurchaseQty, 0);

  const handleExportCSV = () => {
    if (activeTab === 'forecasts' || activeTab === 'recommendations') {
      exportToExcel(
        forecasts.map((f) => ({
          'Item Code': f.itemId,
          'Item Name': f.itemName,
          'Forecast Month': `${f.forecastMonth}/${f.forecastYear}`,
          'Predicted Qty': f.predictedQty,
          'AI Confidence': `${f.confidence}%`,
          Trend: f.trend,
          'Seasonal Factor': f.seasonalFactor ?? 1.0,
          'Suggested Production': f.suggestedProductionQty,
          'Suggested Purchase': f.suggestedPurchaseQty,
        })),
        [
          { header: 'Item Code', key: 'Item Code' },
          { header: 'Item Name', key: 'Item Name' },
          { header: 'Forecast Month', key: 'Forecast Month' },
          { header: 'Predicted Qty', key: 'Predicted Qty' },
          { header: 'AI Confidence', key: 'AI Confidence' },
          { header: 'Trend', key: 'Trend' },
          { header: 'Seasonal Factor', key: 'Seasonal Factor' },
          { header: 'Suggested Production', key: 'Suggested Production' },
          { header: 'Suggested Purchase', key: 'Suggested Purchase' },
        ],
        'ai_demand_forecast_report'
      );
    } else {
      exportToExcel(
        mockSalesTrends.map((t) => ({
          'Item Code': t.itemId,
          'Item Name': t.itemName,
          Period: `${t.month}/${t.year}`,
          'Actual Sales': t.actualSales,
          'Forecast Sales': t.forecastSales,
          Accuracy: `${t.accuracy}%`,
        })),
        [
          { header: 'Item Code', key: 'Item Code' },
          { header: 'Item Name', key: 'Item Name' },
          { header: 'Period', key: 'Period' },
          { header: 'Actual Sales', key: 'Actual Sales' },
          { header: 'Forecast Sales', key: 'Forecast Sales' },
          { header: 'Accuracy', key: 'Accuracy' },
        ],
        'ai_sales_trends_report'
      );
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">AI Demand Forecasting</h2>
            <p className="text-xs text-[#64748b] mt-0.5">
              Machine learning predictions based on historical sales, seasonality, and consumption trends
            </p>
          </div>

          <div className="flex items-center flex-wrap gap-2 text-xs">
            <span className="text-[#64748b] font-medium">Lookback Horizon</span>
            <select
              value={lookbackMonths}
              onChange={(e) => setLookbackMonths(Number(e.target.value))}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none"
            >
              <option value={3}>Last 3 Months</option>
              <option value={6}>Last 6 Months (Recommended)</option>
              <option value={12}>Last 12 Months</option>
            </select>
            <button
              onClick={() => alert('AI Model Retrained with latest historical consumption!')}
              className="h-8 px-3 rounded-lg bg-[#4f46e5] text-white font-medium hover:bg-indigo-700 transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-magic-line text-xs" />
              <span>Re-run AI Model</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="h-8 px-3 rounded-lg border border-[#e2e8f0] bg-white text-[#1e293b] hover:bg-slate-50 transition-colors font-medium flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-download-2-line text-xs" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Top KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">FORECAST ACCURACY</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1">{avgAccuracy}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
              <span>Based on 6-month test</span>
            </div>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">PROJECTED DEMAND</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1">{totalPredictedQty.toLocaleString()} Units</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-indigo-600 font-medium">
              <span>Upcoming Month Forecast</span>
            </div>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">SUGGESTED PRODUCTION</span>
            <div className="text-xl font-bold text-amber-700 mt-1">{totalProductionQty.toLocaleString()} Units</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-amber-600 font-medium">
              <span>Finished Goods replenishment</span>
            </div>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm">
            <span className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">SUGGESTED PURCHASES</span>
            <div className="text-xl font-bold text-[#1e293b] mt-1">{totalPurchaseQty.toLocaleString()} Units</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
              <span>Raw material replenishment</span>
            </div>
          </div>
        </div>

        {/* Tab Pills */}
        <div className="flex items-center gap-2 border-b border-[#e2e8f0] pb-3">
          <button
            onClick={() => setActiveTab('forecasts')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'forecasts' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            Demand Predictions
          </button>
          <button
            onClick={() => setActiveTab('trends')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'trends' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            Historical Sales & Model Accuracy
          </button>
          <button
            onClick={() => setActiveTab('recommendations')}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeTab === 'recommendations' ? 'bg-[#1e293b] text-white shadow-sm' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'
            }`}
          >
            Actionable Order Recommendations
          </button>
        </div>

        {/* TAB 1: Forecasts */}
        {activeTab === 'forecasts' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-3.5 border-b border-[#e2e8f0] flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#1e293b]">Predicted Demand by Product</h3>
              <span className="text-xs text-[#64748b]">Powered by Multi-variate Seasonal Regression</span>
            </div>

            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                    <th className="px-4 py-3">ITEM CODE</th>
                    <th className="px-4 py-3">ITEM NAME</th>
                    <th className="px-4 py-3 text-center">TREND</th>
                    <th className="px-4 py-3 text-right">CONFIDENCE</th>
                    <th className="px-4 py-3 text-right">SEASONAL FACTOR</th>
                    <th className="px-4 py-3 text-right">PREDICTED DEMAND</th>
                    <th className="px-4 py-3 text-right">SUGGESTED PRD</th>
                    <th className="px-4 py-3 text-right">SUGGESTED PO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {forecasts.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3 font-mono text-[#4f46e5] font-semibold">{f.itemId}</td>
                      <td className="px-4 py-3 font-medium text-[#1e293b]">{f.itemName}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            f.trend === 'INCREASING'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : f.trend === 'DECREASING'
                              ? 'bg-rose-50 text-rose-700 border border-rose-200'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {f.trend}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        <span className="text-emerald-600 font-bold">{f.confidence}%</span>
                      </td>
                      <td className="px-4 py-3 text-right text-[#64748b] font-medium">
                        {f.seasonalFactor ? `${f.seasonalFactor.toFixed(2)}x` : '1.00x'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#1e293b]">
                        {f.predictedQty.toLocaleString()} units
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-amber-700">
                        {f.suggestedProductionQty > 0 ? `${f.suggestedProductionQty} units` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                        {f.suggestedPurchaseQty > 0 ? `${f.suggestedPurchaseQty} units` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: Trends */}
        {activeTab === 'trends' && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-3.5 border-b border-[#e2e8f0]">
              <h3 className="text-sm font-semibold text-[#1e293b]">Historical Sales vs AI Prediction Backtesting</h3>
            </div>

            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] uppercase tracking-wider">
                    <th className="px-4 py-3">ITEM</th>
                    <th className="px-4 py-3">MONTH / YEAR</th>
                    <th className="px-4 py-3 text-right">ACTUAL SALES</th>
                    <th className="px-4 py-3 text-right">FORECASTED SALES</th>
                    <th className="px-4 py-3 text-right">MODEL ACCURACY</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {mockSalesTrends.map((t, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/70">
                      <td className="px-4 py-3 font-medium text-[#1e293b]">{t.itemName}</td>
                      <td className="px-4 py-3 text-[#64748b]">
                        {new Date(t.year, t.month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-[#1e293b]">{t.actualSales}</td>
                      <td className="px-4 py-3 text-right text-[#64748b]">{t.forecastSales}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-600">
                        {t.accuracy > 0 ? `${t.accuracy.toFixed(1)}%` : 'Forecast Pending'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: Recommendations */}
        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            {forecasts.map((f) => (
              <div
                key={f.id}
                className="bg-white border border-[#e2e8f0] rounded-xl p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-[#1e293b]">{f.itemName}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600 font-mono">
                      {f.itemId}
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {f.confidence}% confidence
                    </span>
                  </div>
                  <p className="text-xs text-[#64748b] mt-1.5">
                    Predicted next month demand: <span className="font-semibold text-[#1e293b]">{f.predictedQty} units</span>.
                    {f.suggestedProductionQty > 0 && ` Suggested replenishment: create production order for ${f.suggestedProductionQty} units.`}
                    {f.suggestedPurchaseQty > 0 && ` Suggested replenishment: create procurement order for ${f.suggestedPurchaseQty} units.`}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {f.suggestedProductionQty > 0 && (
                    <button
                      onClick={() => navigate('/manufacturing/orders')}
                      className="px-3.5 py-2 rounded-lg bg-[#1e293b] text-white text-xs font-semibold hover:bg-[#334155] transition-colors cursor-pointer flex items-center gap-1.5"
                    >
                      <i className="ri-settings-line text-xs" />
                      <span>Create Production Order</span>
                    </button>
                  )}
                  {f.suggestedPurchaseQty > 0 && (
                    <button
                      onClick={() => navigate('/purchase/orders/new')}
                      className="px-3.5 py-2 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors cursor-pointer flex items-center gap-1.5"
                    >
                      <i className="ri-shopping-bag-line text-xs" />
                      <span>Create Purchase Order</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/manufacturing/mrp')}
                    className="px-3 py-2 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#475569] hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    Run MRP
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
